import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Popover } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  colorTokens,
  getSpacing,
  getShadow,
  typographyTokens,
} from "../../../designTokens";
import { getProfilePhotoApi } from "../../../Apis/profilePhoto";
import { useAppSelector } from "../../../redux/hooks";
import { performLogout } from "../../../auth/performLogout";
import { ProfileDrawer } from "../ProfileDrawer/index";
import { canSeeConfigurationNav } from "../../../utils/teamLeaderAccess";
import {
  canSeeDashboardNav,
  getDashboardPathForRoles,
} from "../../../utils/dashboardAccess";
import {
  applySidebarNotificationBadges,
  canSeeMyTasksNav,
} from "../../../utils/notificationBadges";

// Import SVG icons
import careProLogo from "../../../Assets/icons/careProLogo.svg";
import DashboardIcon from "../../../Assets/icons/DashboardIcon.svg";
import DashboardActiveIcon from "../../../Assets/icons/DashboardActiveIcon.svg";
import LeadActiveIcon from "../../../Assets/icons/LeadActiveIcon.svg";
import LeadIcon from "../../../Assets/icons/LeadIcon.svg";
import LeadHoverIcon from "../../../Assets/icons/LeadHoverIcon.svg";
import agentTaskActiveIcon from "../../../Assets/icons/agentTaskActiveIcon.svg";
import agentTaskIcon from "../../../Assets/icons/agentTaskIcon.svg";
import OverviewIcon from "../../../Assets/icons/OverviewIcon.svg";
import OverviewActiveIcon from "../../../Assets/icons/OverviewActiveIcon.svg";
import settingsIcon from "../../../Assets/icons/settingIcon.png";
import TallyIcon from "../../../Assets/icons/TallyIcon.png";

// Navigation item interface
export interface NavItem {
  id: string;
  label: string;
  path: string;
  iconDefault: string;
  iconHover?: string;
  iconActive?: string;
  badge?: number;
  position?: "main" | "footer";
}

// Sidebar props interface
export interface SidebarProps {
  navItems?: NavItem[];
  userProfile?: {
    name?: string;
    avatarUrl?: string;
    isOnline?: boolean;
  };
  idPrefix?: string;
  hideHeaderNavFooter?: boolean;
}

// Default navigation items (Dashboard first, then Lead, Overview, Tasks)
const defaultNavItems: NavItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/home",
    iconDefault: DashboardIcon,
    iconHover: DashboardActiveIcon,
    iconActive: DashboardActiveIcon,
    position: "main",
  },
  {
    id: "leads",
    label: "Opportunities",
    path: "/my-leads",
    iconDefault: LeadIcon,
    iconHover: LeadHoverIcon,
    iconActive: LeadActiveIcon,
    position: "main",
  },
  {
    id: "overview",
    label: "Overview",
    path: "/overview",
    iconDefault: OverviewIcon,
    iconHover: OverviewActiveIcon,
    iconActive: OverviewActiveIcon,
    position: "main",
  },
  {
    id: "spocTasks",
    label: "My Tasks",
    path: "/spoc-tasks",
    iconDefault: agentTaskIcon,
    iconHover: agentTaskIcon,
    iconActive: agentTaskActiveIcon,
    position: "main",
  },
  {
    id: "Configurations",
    label: "Configurations",
    path: "/configurations",
    iconDefault: settingsIcon,
    iconHover: settingsIcon,
    iconActive: settingsIcon,
    position: "footer",
  },
  {
    id: "TallyForm",
    label: "Support",
    path: "https://prodigyhealth.notion.site/Paras-Carepro-Request-Tracker-3110741628ff81668141cf6d2c007a01",
    iconDefault: TallyIcon,
    iconHover: TallyIcon,
    iconActive: TallyIcon,
    position: "footer",
  },
];

// Helper to get email string from user (handles both { value: string } and string)
const getEmailFromUser = (
  user: { email?: { value?: string } | string } | null
): string => {
  if (!user?.email) return "";
  const e = user.email;
  return typeof e === "string" ? e : ((e as { value?: string })?.value ?? "");
};

const Sidebar: React.FC<SidebarProps> = ({
  navItems = defaultNavItems,
  userProfile: userProfileProp,
  idPrefix = "sidebar",
  hideHeaderNavFooter = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userProfileFromStore = useAppSelector((state) => state.userProfile);
  const notifUnreadCount = useAppSelector(
    (state: any) =>
      state.notifications?.unreadCount ?? { total: 0, MY_LEADS: 0, MY_TASKS: 0 }
  );
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [sidebarProfilePhotoUrl, setSidebarProfilePhotoUrl] = useState<
    string | null
  >(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch profile photo for sidebar avatar (same as ProfileDrawer)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getProfilePhotoApi();
        if (!cancelled) {
          setSidebarProfilePhotoUrl(res?.data?.url ?? null);
        }
      } catch {
        if (!cancelled) setSidebarProfilePhotoUrl(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch profile photo when profile drawer closes (e.g. after user uploads new photo)
  const prevDrawerOpen = useRef(profileDrawerOpen);
  useEffect(() => {
    if (prevDrawerOpen.current && !profileDrawerOpen) {
      getProfilePhotoApi()
        .then((res) => setSidebarProfilePhotoUrl(res?.data?.url ?? null))
        .catch(() => setSidebarProfilePhotoUrl(null));
    }
    prevDrawerOpen.current = profileDrawerOpen;
  }, [profileDrawerOpen]);

  // Derive display name and email from store
  const displayName =
    (userProfileFromStore?.user?.representative?.fullName ??
      (getEmailFromUser(userProfileFromStore?.user ?? null) || "").split(
        "@"
      )[0]) ||
    "User";
  const avatarUrl =
    userProfileProp?.avatarUrl ?? sidebarProfilePhotoUrl ?? undefined;
  const isOnline = userProfileProp?.isOnline ?? true;

  // Handle navigation click
  const handleNavClick = (path: string) => {
    navigate(path);
  };

  // Handle profile button click
  const handleProfileClick = () => {
    setProfilePopoverOpen(!profilePopoverOpen);
  };

  // Handle name/email click - open Profile drawer
  const handleNameClick = () => {
    setProfileDrawerOpen(true);
    setProfilePopoverOpen(false);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await performLogout({ callLogoutApi: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      navigate("/");
      setProfilePopoverOpen(false);
    }
  };

  // Close popover when clicking outside
  const handlePopoverClose = () => {
    setProfilePopoverOpen(false);
  };

  const isPathActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const isNavItemActive = (item: NavItem): boolean => {
    if (item.id === "dashboard") {
      return isPathActive("/home") || isPathActive("/team-dashboard");
    }
    return isPathActive(item.path);
  };

  // Get the appropriate icon based on state
  const getIconSrc = (
    item: NavItem,
    isActive: boolean,
    isHovered: boolean
  ): string => {
    if (isActive && item.iconActive) {
      return item.iconActive;
    }
    if (isHovered && item.iconHover) {
      return item.iconHover;
    }
    return item.iconDefault;
  };

  // Render a single navigation icon
  const NavIcon: React.FC<{
    iconSrc: string;
    iconId: string;
    isActive: boolean;
    isHovered: boolean;
  }> = ({ iconSrc, iconId, isActive, isHovered }) => {
    // Smaller size for appointments and notifications icons and footer icons (Configurations, TallyForm)
    const iconSize =
      iconId === "appointments" ||
      iconId === "notifications" ||
      iconId === "Configurations" ||
      iconId === "TallyForm"
        ? "20px"
        : "48px";
    const minIconSize =
      iconId === "appointments" ||
      iconId === "notifications" ||
      iconId === "Configurations" ||
      iconId === "TallyForm"
        ? "20px"
        : "40px";
    // spocTasks icon at 40% size for consistent visual weight
    const iconScale = iconId === "spocTasks" ? 0.4 : 1;
    return (
      <div
        style={{
          width: iconSize,
          height: iconSize,
          minWidth: minIconSize,
          minHeight: minIconSize,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
        id={`${idPrefix}-icon-wrapper-${iconId}`}
      >
        <img
          src={iconSrc}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            transition: "all 0.2s ease-in-out",
            opacity: isActive ? 1 : isHovered ? 0.9 : 0.85,
            transform: `scale(${iconScale})`,
          }}
          aria-hidden="true"
          id={`${idPrefix}-icon-${iconId}`}
        />
      </div>
    );
  };

  // Render a single navigation button
  const NavButton: React.FC<{ item: NavItem }> = ({ item }) => {
    const isActive = isNavItemActive(item);
    const isHovered = hoveredItemId === item.id;
    const iconSrc = getIconSrc(item, isActive, isHovered);

    return (
      <div
        key={item.id}
        className="relative flex items-center justify-center"
        style={{ width: "100%" }}
      >
        {/* Navigation button */}
        <button
          type="button"
          onClick={() => {
            if (
              item.path.startsWith("http://") ||
              item.path.startsWith("https://")
            ) {
              window.open(item.path, "_blank", "noopener,noreferrer");
            } else {
              handleNavClick(item.path);
            }
          }}
          onMouseEnter={() => setHoveredItemId(item.id)}
          onMouseLeave={() => setHoveredItemId(null)}
          className="flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 relative"
          style={{
            width: "56px",
            height: "56px",
            borderRadius: getSpacing("12px"),
            backgroundColor: isActive
              ? colorTokens["Background/Bluish"]
              : isHovered
                ? colorTokens["Background/Greyish"]
                : "transparent",
          }}
          id={`${idPrefix}-nav-${item.id}`}
          aria-label={item.label}
          aria-current={isActive ? "page" : undefined}
        >
          <NavIcon
            iconSrc={iconSrc}
            iconId={item.id}
            isActive={isActive}
            isHovered={isHovered}
          />

          {/* Badge notification */}
          {item.badge && item.badge > 0 && (
            <span
              className="absolute top-1 right-1 text-white text-[10px] font-medium rounded-full w-5 h-5 flex items-center justify-center"
              style={{
                backgroundColor: colorTokens["Alert/Red/Dark"],
              }}
              id={`${idPrefix}-badge-${item.id}`}
            >
              {item.badge > 9 ? "9+" : item.badge}
            </span>
          )}
        </button>
        {isHovered && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "56px",
              marginLeft: getSpacing("8px"),
              transform: "translateY(-50%)",
              paddingTop: getSpacing("4px"),
              paddingBottom: getSpacing("4px"),
              paddingLeft: getSpacing("12px"),
              paddingRight: getSpacing("12px"),
              borderRadius: getSpacing("4px"),
              backgroundColor: colorTokens["Primary/Default"],
              boxShadow: getShadow("Shadow"),
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap",
              zIndex: 1000,
            }}
            id={`${idPrefix}-nav-label-${item.id}`}
          >
            <span
              style={{
                fontFamily: typographyTokens["Body/Small"].family,
                fontSize: `${typographyTokens["Body/Small"].size}px`,
                fontWeight: typographyTokens["Body/Small"].weight,
                lineHeight: typographyTokens["Body/Small"].lineHeight,
                color: colorTokens["Background/White"],
              }}
            >
              {item.label}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Configuration is visible only for TENANT_ADMIN and FACILITY_ADMIN roles
  const userRoles = userProfileFromStore?.user?.roles ?? [];
  const hasSpocRole = Array.isArray(userRoles) && userRoles.includes("SPOC");
  const unitPocActions =
    userProfileFromStore?.permissionGroup?.modulePermissions?.["UNIT_POC"]
      ?.actions ?? [];
  const canSpocSeeMyLeads =
    unitPocActions.includes("AGENT") || unitPocActions.includes("MANAGE");
  const canShowLeadsNav = hasSpocRole ? canSpocSeeMyLeads : true;
  const tasksModuleEnabled =
    userProfileFromStore?.permissionGroup?.modulePermissions?.TASKS?.enabled ===
    true;
  const canViewTasks = canSeeMyTasksNav({
    roles: userRoles,
    unitPocActions,
    tasksModuleEnabled,
  });
  const displayRole =
    userRoles.length > 0
      ? userRoles[0].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "";
  const canSeeConfiguration = canSeeConfigurationNav(userProfileFromStore);
  const canSeeDashboard = canSeeDashboardNav(userRoles);
  const dashboardPath = getDashboardPathForRoles(userRoles);

  // Inject live unread badge counts from Redux notification state
  const navItemsWithDashboardPath = (navItems ?? defaultNavItems).map(
    (item) => {
      if (item.id === "dashboard" && dashboardPath) {
        return { ...item, path: dashboardPath };
      }
      return item;
    }
  );
  const navItemsWithBadges = applySidebarNotificationBadges(
    navItemsWithDashboardPath,
    notifUnreadCount
  );

  // Separate main and footer nav items. Overview is visible for all users.
  const mainNavItems = navItemsWithBadges
    .filter((item) => item.position !== "footer")

    .filter((item) => {
      if (item.id === "dashboard") return canSeeDashboard;
      if (item.id === "overview") return true;
      if (item.id === "leads") return canShowLeadsNav;
      if (item.id === "spocTasks") return canViewTasks;
      return false;
    });
  const footerNavItems = navItemsWithBadges
    .filter((item) => item.position === "footer")
    .filter((item) => item.id !== "Configurations" || canSeeConfiguration);

  // If hideHeaderNavFooter is true, don't render the aside to avoid empty navigation landmark
  if (hideHeaderNavFooter) {
    return null;
  }

  return (
    <aside
      className="h-screen min-h-screen flex flex-col"
      style={{
        width: "80px",
        backgroundColor: colorTokens["Background/White"],
        borderRight: `1px solid ${colorTokens["Borders/Light"]}`,
      }}
      id={`${idPrefix}-container`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header section with logo */}
      <div
        className="flex flex-col items-center"
        style={{
          paddingTop: getSpacing("24px"),
          paddingBottom: getSpacing("16px"),
          paddingLeft: getSpacing("16px"),
          paddingRight: getSpacing("16px"),
        }}
        id={`${idPrefix}-header`}
      >
        {/* Logo button */}
        <button
          type="button"
          // onClick={() => handleNavClick("/agent-dashboard")}
          className="rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            padding: getSpacing("8px"),
          }}
          id={`${idPrefix}-logo-button`}
          aria-label="Go to dashboard"
        >
          <div
            className="relative flex items-center justify-center"
            style={{
              width: "40px",
              height: "40px",
            }}
            id={`${idPrefix}-logo-container`}
          >
            <img
              src={careProLogo}
              alt="CarePro Logo"
              style={{
                width: "40px",
                height: "40px",
              }}
              id={`${idPrefix}-logo`}
            />
          </div>
        </button>
      </div>

      {/* Top divider */}
      <div
        style={{
          height: "1px",
          backgroundColor: colorTokens["Borders/Light"],
          marginLeft: getSpacing("16px"),
          marginRight: getSpacing("16px"),
        }}
        id={`${idPrefix}-divider-top`}
        aria-hidden="true"
      />

      {/* Main navigation section */}
      <nav
        className="flex flex-col items-center"
        style={{
          paddingTop: getSpacing("24px"),
          paddingBottom: getSpacing("24px"),
          paddingLeft: getSpacing("12px"),
          paddingRight: getSpacing("12px"),
          gap: getSpacing("12px"),
        }}
        id={`${idPrefix}-nav-main`}
      >
        {mainNavItems.map((item) => (
          <NavButton key={item.id} item={item} />
        ))}
      </nav>

      {/* Spacer to push footer down */}
      <div className="flex-1" aria-hidden="true" />

      {/* Bottom divider */}
      <div
        style={{
          height: "1px",
          backgroundColor: colorTokens["Borders/Light"],
          marginLeft: getSpacing("16px"),
          marginRight: getSpacing("16px"),
        }}
        id={`${idPrefix}-divider-bottom`}
        aria-hidden="true"
      />

      {/* Footer section with notifications and profile */}
      <div
        className="flex flex-col items-center"
        style={{
          paddingTop: getSpacing("24px"),
          paddingBottom: getSpacing("24px"),
          paddingLeft: getSpacing("12px"),
          paddingRight: getSpacing("12px"),
          gap: getSpacing("16px"),
        }}
        id={`${idPrefix}-footer`}
      >
        {/* Footer nav items */}
        {footerNavItems.map((item) => (
          <NavButton key={item.id} item={item} />
        ))}

        {/* Profile avatar - first letter when no image, image when available */}
        <button
          ref={profileButtonRef}
          type="button"
          onClick={handleProfileClick}
          className="relative focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-full"
          style={{ marginTop: getSpacing("8px") }}
          id={`${idPrefix}-profile-button`}
          aria-label="User profile"
          aria-expanded={profilePopoverOpen}
          aria-haspopup="true"
        >
          <div
            className="rounded-full flex items-center justify-center overflow-hidden"
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: avatarUrl
                ? "transparent"
                : colorTokens["Background/Bluish"],
            }}
            id={`${idPrefix}-profile-avatar`}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName || "User avatar"}
                style={{
                  width: "32px",
                  height: "32px",
                  objectFit: "cover",
                }}
                id={`${idPrefix}-profile-image`}
              />
            ) : (
              <span
                style={{
                  fontFamily: typographyTokens["Body/Medium"].family,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: colorTokens["Primary/Default"],
                  lineHeight: 1,
                }}
                id={`${idPrefix}-profile-initial`}
              >
                {(displayName || "U").charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          {/* Online status indicator */}
          {isOnline && (
            <div
              className="absolute rounded-full"
              style={{
                width: "12px",
                height: "12px",
                bottom: 0,
                right: 0,
                backgroundColor: "#22C55E",
                border: `2px solid ${colorTokens["Background/White"]}`,
              }}
              id={`${idPrefix}-profile-status`}
              aria-label="Online"
            />
          )}
        </button>

        {/* Profile Popover - matches Figma design (node 7699:100384) */}
        <Popover
          open={profilePopoverOpen}
          anchorEl={profileButtonRef.current}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          PaperProps={{
            style: {
              marginRight: getSpacing("8px"),
              borderRadius: getSpacing("12px"),
              border: `1px solid ${colorTokens["Borders/Light Grey"]}`,
              boxShadow: getShadow("Shadow"),
              minWidth: "260px",
              padding: `${getSpacing("12px")} 0`,
              display: "flex",
              flexDirection: "column",
              gap: 0,
            },
            id: `${idPrefix}-popover`,
          }}
        >
          {/* Name + Email section (clickable - opens Profile drawer) - full column hover */}
          <button
            type="button"
            onClick={handleNameClick}
            style={{
              padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
              width: "100%",
              textAlign: "left",
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: getSpacing("8px"),
              transition: "background-color 0.2s ease",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                colorTokens["Background/Greyish"];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            id={`${idPrefix}-profile-name-button`}
            aria-label="Open profile"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getSpacing("4px"),
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontFamily: typographyTokens["Body/Medium"].family,
                  fontSize: `${typographyTokens["Body/Medium"].size}px`,
                  fontWeight: typographyTokens["Body/Medium"].weight,
                  lineHeight: typographyTokens["Body/Medium"].lineHeight,
                  color: colorTokens["Text/Black"],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </span>
              {displayRole && (
                <span
                  style={{
                    fontFamily: typographyTokens["Body/Small"].family,
                    fontSize: `${typographyTokens["Body/Small"].size}px`,
                    fontWeight: typographyTokens["Body/Small"].weight,
                    lineHeight: typographyTokens["Body/Small"].lineHeight,
                    color: colorTokens["Text/Light Grey"],
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayRole}
                </span>
              )}
            </div>
            <ChevronRightIcon
              style={{
                fontSize: "20px",
                color: colorTokens["Text/Light Grey"],
                flexShrink: 0,
              }}
              aria-hidden="true"
            />
          </button>

          {/* Logout button - full column hover */}
          <button
            type="button"
            onClick={handleLogout}
            style={{
              padding: `${getSpacing("12px")} ${getSpacing("16px")}`,
              width: "100%",
              textAlign: "left",
              border: "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              fontFamily: typographyTokens["Body/Medium"].family,
              fontSize: `${typographyTokens["Body/Medium"].size}px`,
              fontWeight: typographyTokens["Body/Medium"].weight,
              lineHeight: typographyTokens["Body/Medium"].lineHeight,
              color: colorTokens["Alert/Red/Dark"],
              transition: "background-color 0.2s ease",
              boxSizing: "border-box",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                colorTokens["Background/Greyish"];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            id={`${idPrefix}-logout-button`}
            aria-label="Logout"
          >
            Logout
          </button>
        </Popover>

        {/* Profile Drawer */}
        <ProfileDrawer
          isOpen={profileDrawerOpen}
          onClose={() => setProfileDrawerOpen(false)}
          userData={userProfileFromStore}
          idPrefix={idPrefix}
        />
      </div>
    </aside>
  );
};

export default Sidebar;
